import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth, db } from '../services/firebase';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import AppNavBar from './AppNavBar';

const SuiteDashboard = () => {
  const navigate = useNavigate();
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Admin bypass - add your email here
  const ADMIN_EMAILS = [
    'thomasfairgrieve3@gmail.com',
    'adriandalzell@gmail.com',
    'mark.gomersall@newdur.ac.uk',
    // Add more admin emails as needed
  ];

  useEffect(() => {
    const fetchUserData = async () => {
      const user = auth.currentUser;
      if (!user) {
        navigate('/login');
        return;
      }

      try {
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        if (userDoc.exists()) {
          setUserData(userDoc.data());
        } else {
          // Create new user document with default values
          const newUserData = {
            email: user.email,
            subscriptionTier: 'free',
            assessUsage: 0,
            plannerUsage: 0,
            createdAt: new Date().toISOString(),
            stripeCustomerId: null,
            subscriptionId: null
          };
          await setDoc(doc(db, 'users', user.uid), newUserData);
          setUserData(newUserData);
        }
      } catch (error) {
        console.error('Error fetching user data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, [navigate]);

  const checkAccessAndNavigate = async (tool) => {
    const user = auth.currentUser;
    if (!user) return;
  
    // Admin bypass
    if (ADMIN_EMAILS.includes(user.email)) {
      navigate(`/${tool}`);
      return;
    }
  
    if (!userData) return;
  
    const isSubscribed = userData.subscriptionStatus === 'active' ||
      userData.subscriptionTier === 'premium' ||
      userData.subscriptionTier === 'premiumPlus';
  
    if (!isSubscribed) {
      navigate('/upgrade');
      return;
    }
  
    navigate(`/${tool}`);
  };

  const handleAssessClick = () => {
    checkAccessAndNavigate('assess');
  };

  const handlePlannerClick = () => {
    checkAccessAndNavigate('planner');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-sand-100 to-sand-200">
        <div className="text-ink-800 text-lg sm:text-xl">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-sand-100 to-sand-200">
      <AppNavBar currentPage="dashboard" />
      
      <div className="flex items-center justify-center py-6 sm:py-8 md:py-12 px-2 sm:px-4">
        <div className="max-w-5xl mx-auto">
          {/* Header */}
          <div className="text-center mb-8 sm:mb-10 md:mb-12">
            <h1 className="text-2xl sm:text-3xl md:text-5xl lg:text-6xl font-bold text-coral-500 leading-tight mb-4 sm:mb-6 md:mb-8 px-2">
              Welcome to TeAIch Suite
            </h1>
            <p className="text-base sm:text-lg md:text-xl text-ink-700 px-2">Choose an app to get started</p>
          </div>

          {/* Two App Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 md:gap-8 max-w-4xl mx-auto">
            
            {/* TeAIch Assess Card */}
            <div 
              onClick={handleAssessClick}
              className="bg-white rounded-lg border-4 border-ink-900 shadow-brutal hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1 cursor-pointer group"
            >
              <div className="p-6 sm:p-8 md:p-12 text-center">
                {/* Logo */}
                <div className="mb-4 sm:mb-6 md:mb-8">
                  <div className="w-16 h-16 sm:w-20 sm:h-20 md:w-24 md:h-24 mx-auto bg-gradient-to-br from-coral-300 to-coral-300 rounded-full flex items-center justify-center group-hover:from-coral-500 group-hover:to-coral-600 transition-all duration-300 overflow-hidden">
                    <img 
                      src="/images/TeaichAssessAppLogo.png" 
                      alt="TeAIch Assess Logo" 
                      className="w-10 h-10 sm:w-12 sm:h-12 md:w-16 md:h-16 object-contain"
                    />
                  </div>
                </div>

                {/* Content */}
                <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-ink-800 mb-3 sm:mb-4 group-hover:text-coral-600 transition-colors">
                  TeAIch Assess
                </h2>
                <p className="text-ink-600 text-sm sm:text-base md:text-lg leading-relaxed mb-3 sm:mb-4">
                  AI-powered marking and assessment tools for educators. Upload student work and get detailed feedback.
                </p>
            
                {/* Button */}
                <div className="bg-coral-300 text-white px-4 sm:px-6 md:px-8 py-2 sm:py-3 md:py-4 rounded-lg font-semibold text-sm sm:text-base md:text-lg group-hover:bg-coral-500 transition-all duration-300 inline-block mt-3 sm:mt-4">
                  Open Assess →
                </div>
              </div>
            </div>

            {/* TeAIch Planner Card */}
            <div 
              onClick={handlePlannerClick}
              className="bg-white rounded-lg border-4 border-ink-900 shadow-brutal hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1 cursor-pointer group"
            >
              <div className="p-6 sm:p-8 md:p-12 text-center">
                {/* Logo */}
                <div className="mb-4 sm:mb-6 md:mb-8">
                  <div className="w-16 h-16 sm:w-20 sm:h-20 md:w-24 md:h-24 mx-auto bg-gradient-to-br from-coral-300 to-coral-300 rounded-full flex items-center justify-center group-hover:from-coral-500 group-hover:to-coral-600 transition-all duration-300 overflow-hidden">
                    <img 
                      src="/images/TeaichPlannerAppLogo.png" 
                      alt="TeAIch Planner Logo" 
                      className="w-10 h-10 sm:w-12 sm:h-12 md:w-16 md:h-16 object-contain"
                    />
                  </div>
                </div>
                
                {/* Content */}
                <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-ink-800 mb-3 sm:mb-4 group-hover:text-coral-600 transition-colors">
                  TeAIch Planner
                </h2>
                <p className="text-ink-600 text-sm sm:text-base md:text-lg leading-relaxed mb-3 sm:mb-4">
                  AI-powered lesson planning and curriculum tools. Create engaging lessons with intelligent assistance.
                </p>
              
                {/* Button */}
                <div className="bg-coral-300 text-white px-4 sm:px-6 md:px-8 py-2 sm:py-3 md:py-4 rounded-lg font-semibold text-sm sm:text-base md:text-lg group-hover:bg-coral-500 transition-all duration-300 inline-block mt-3 sm:mt-4">
                  Open Planner →
                </div>
              </div>
            </div>
          </div>

          {/* Optional: Quick Welcome Message */}
          <div className="mt-8 sm:mt-12 md:mt-16 text-center px-2">
            <div className="bg-white bg-opacity-70 rounded-lg border-4 border-ink-900 shadow-md p-4 sm:p-6 max-w-2xl mx-auto">
              <p className="text-ink-700 text-sm sm:text-base md:text-lg">
                <span className="font-semibold">Ready to revolutionize your teaching?</span> 
                <br />
                Choose TeAIch Assess to start marking, or TeAIch Planner to create lessons.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SuiteDashboard;